// @ts-strict-ignore
import sumBy from 'lodash/sumBy';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import some from 'lodash/some';

import {Bar, BarPattern, BarSegment, Segment} from 'common/charts/visualization/bar-viz/types';
import {RESULTS_OVERALL_SUM_KEY, ValueRepresentationType} from 'common/report/constants';
import {SegmentFilterController, SegmentItem, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {AggregatedSeriesValue, GroupClause, InsightsQueryResults} from 'common/report/insights/util/types';
import {
  createUnorderedSumTree,
  getAggregatedSeriesList,
  getNestedHeaderTypes,
  getSortedAggregatedSeriesKeys,
  getSortedKeyMap,
} from 'common/report/insights/util';
import {renamePropertyOld, renamePropertyValue} from 'common/report/util/properties';
import {formatMetricNumber, formatPercent} from 'common/util';
import {colors, getChartSegmentColorCombo} from 'common/util/colors';
import {FlatSortConfig, OrderedSumTree, SortBy, SortConfig, UnorderedSumTree} from 'common/report/util/sorting/types';
import {LiftType} from 'common/report/util/lift/types';

import type {LegendData, SegmentExtras} from './types';

export function getSumTree(results: InsightsQueryResults): UnorderedSumTree {
  const headerTypes = getNestedHeaderTypes(results.series);

  let baselineKey = null;
  if (results.meta.liftComparison?.type === LiftType.Segment) {
    baselineKey = results.meta.liftComparison?.groupValue || results.meta.liftComparison?.cohort?.name;
  } else if (results.meta.liftComparison?.type === LiftType.Overall) {
    baselineKey = RESULTS_OVERALL_SUM_KEY;
  }

  const sumTree = createUnorderedSumTree(
    results.series,
    results.secondary?.series,
    results.time_comparison?.series,
    results.secondary_time_comparison?.series,
    results.lift_comparison?.series,
    baselineKey,
    headerTypes,
  );
  return sumTree;
}

export function getRelatedSegmentIdsPerSection(
  depth: number,
  aggregatedSeriesValue: AggregatedSeriesValue,
): string[][] {
  const relatedSegmentsIdsPerSection = new Array(depth).fill(null).map(() => new Set<string>());
  const {paths} = aggregatedSeriesValue;

  /**
   * All paths should be the same length.
   * Iterate over each individual path in reverse starting with the second to last segment (to exclude current segment)
   * and add to the corresponding set of selected segments for sectionIdx
   */
  let pathIdx = paths[0].length - 2;
  let sectionIdx = depth - paths[0].length + 1;

  while (pathIdx >= 0) {
    for (const path of paths) {
      relatedSegmentsIdsPerSection[sectionIdx].add(path[pathIdx]);
    }

    pathIdx--;
    sectionIdx++;
  }

  return relatedSegmentsIdsPerSection.map((set) => [...set]);
}

export function getLegendData({
  apiResultsTruncated,
  groupClauses,
  headers,
  resultHeaders,
  sortConfig,
  sumTree,
}: {
  apiResultsTruncated?: boolean;
  groupClauses: GroupClause[];
  headers: string[];
  resultHeaders: string[];
  sortConfig: SortConfig;
  sumTree: UnorderedSumTree;
}): LegendData {
  const aggregatedSeriesList = getAggregatedSeriesList(sumTree);
  const {colSortAttrs} = sortConfig;

  const sortedKeyMaps = aggregatedSeriesList.map(getSortedKeyMap);

  const sections: SegmentSection[] = aggregatedSeriesList.map((series, idx) => {
    const sortOption =
      sortConfig.sortBy === SortBy.Value ? {sortBy: SortBy.Value, sortOrder: sortConfig.sortOrder} : colSortAttrs[idx];

    const seriesKeys = getSortedAggregatedSeriesKeys(series, sortOption as FlatSortConfig);

    const items: SegmentItem[] = seriesKeys
      .filter((key) => key !== RESULTS_OVERALL_SUM_KEY)
      .map((key) => {
        return {
          id: key,
          label: renamePropertyValue(key, resultHeaders[idx], groupClauses[idx - 1]),
          relatedSegmentsPerSection: getRelatedSegmentIdsPerSection(aggregatedSeriesList.length, series[key]),
        };
      });

    const readOnlyItems = [];

    return {
      label: renamePropertyOld(headers[idx]),
      metadata: sortedKeyMaps[idx],
      items,
      apiResultsTruncated,
      // '12 of top 1000` doesn't make sense for bar charts
      suppressShowingTop: true,
      readOnlyItems,
    };
  });

  return {
    // reverse sections so that last breakdown is the first section
    sortedKeyMaps: sortedKeyMaps.reverse(),
    sections: sections.reverse(),
  };
}

function isEmptyChildren(children: UnorderedSumTree) {
  if (isEmpty(children)) {
    return true;
  }

  // if $overall is the only remaining key, consider the child tree empty
  const keys = Object.keys(children);
  return keys.length === 1 && keys[0] === RESULTS_OVERALL_SUM_KEY;
}

/**
 * Given a sum tree and segmentFilterController, recurse through the tree and
 * filter - out any unselected segments.
 * summarize - because the tree changes, some sub-totals of selected segments will be different
 *
 * The sum tree should not have $overall nodes, but the unsegmented property should be set
 */
export function filterToSelectedSegments(
  tree: UnorderedSumTree,
  segmentFilterController: SegmentFilterController,
): UnorderedSumTree {
  function helper(
    subTree: UnorderedSumTree,
    segmentIdx: number,
    currPath: string[],
  ): {
    children: UnorderedSumTree;
    allChildrenSelected?: boolean;
  } {
    const transformedTree: UnorderedSumTree = {};
    const helperResult = {
      children: transformedTree,
      allChildrenSelected: false,
    };

    if (!subTree) {
      return helperResult;
    }

    const nodes = Object.values(subTree);
    // there should never be any $overall nodes in the tree
    const [selectedSegments, unselectedSegments] = partition(nodes, (node) => {
      return segmentFilterController.isItemSelected(segmentIdx, node.label);
    });

    helperResult.allChildrenSelected = unselectedSegments.length === 0;

    for (const segment of selectedSegments) {
      const path = [...currPath, segment.label];
      if (segmentIdx === 0) {
        transformedTree[segment.label] = {...segment, path};
      } else {
        const childHelperResult = helper(segment.children, segmentIdx - 1, path);

        if (!isEmptyChildren(childHelperResult.children)) {
          const {unsegmented} = segment;

          const childNodes = Object.values(childHelperResult.children);
          let value = 0,
            timeComparisonValue = 0;

          const nonOverallChildNodes = childNodes.filter((node) => !node.path.includes(RESULTS_OVERALL_SUM_KEY));

          const sumOfChildren = {
            value: sumBy(nonOverallChildNodes, (childNode) => (childNode.sumOfChildren || childNode).value),
            timeComparisonValue: sumBy(
              nonOverallChildNodes,
              (childNode) => (childNode.sumOfChildren || childNode).timeComparisonValue,
            ),
            liftComparisonValue: sumBy(
              nonOverallChildNodes,
              (childNode) => (childNode.sumOfChildren || childNode).liftComparisonValue,
            ),
          };

          value = unsegmented.value;
          timeComparisonValue = unsegmented.timeComparisonValue;
          // lift comparison doesn't apply to $overall

          transformedTree[segment.label] = {
            ...segment,
            unsegmented,
            sumOfChildren,
            children: childHelperResult.children,
            value,
            timeComparisonValue,
            path,
          };
        }
      }
    }

    return helperResult;
  }

  return helper(tree, segmentFilterController.getSections().length - 1, []).children;
}

function getBarVizBarSegments({
  node,
  valueRepresentationType,
  leafSortedKeyMap,
  parentValue,
  parentTimeComparisonValue,
  formatter,
  segmentFilterController,
}: {
  node: OrderedSumTree;
  valueRepresentationType: ValueRepresentationType;
  leafSortedKeyMap: {[key: string]: number};
  parentValue: number;
  parentTimeComparisonValue: number;
  formatter: (path: string[], value: number) => string;
  segmentFilterController?: SegmentFilterController;
}): {
  segment: BarSegment<SegmentExtras>;
  timeComparisonSegment?: BarSegment<SegmentExtras>;
  liftComparisonSegment?: BarSegment<SegmentExtras>;
} {
  const segmentColor =
    node.label === OTHER_SEGMENT_VALUE
      ? colors.otherSegment.primary
      : segmentFilterController
        ? segmentFilterController.getItemColor(0, node.label)
        : getChartSegmentColorCombo(leafSortedKeyMap[node.label]).primary;

  function getLabelAndValue({
    value,
    percentage,
    addPercentageSign,
    formatter: formatterFunc,
  }: {
    value: number;
    percentage: number;
    addPercentageSign?: boolean;
    formatter?: (path: string[], value: number) => string;
  }): Pick<BarSegment, 'value' | 'label'> {
    const labelAndValue =
      valueRepresentationType === ValueRepresentationType.Absolute
        ? {value, label: formatMetricNumber(value)}
        : {value: percentage, label: formatPercent(percentage / 100)};

    if (addPercentageSign) {
      labelAndValue.label = `${labelAndValue.label}%`;
    }
    if (formatterFunc) {
      labelAndValue.label = formatterFunc(node.path, labelAndValue.value);
    }
    return labelAndValue;
  }

  const percentage = (node.value / parentValue) * 100;
  const timeComparisonPercentage = (node.timeComparisonValue / parentTimeComparisonValue || 0) * 100;

  const extras: SegmentExtras = {
    path: node.path,
    value: node.value,
    secondaryValue: node.secondaryValue,
    timeComparisonValue: node.timeComparisonValue,
    secondaryTimeComparisonValue: node.secondaryTimeComparisonValue,
    liftComparisonValue: node.liftComparisonValue,
    percentage,
    timeComparisonPercentage,
    isTimeComparisonSegment: false,
    isLiftComparisonSegment: false,
  };

  const segment = {
    extras,
    color: segmentColor,
    ...getLabelAndValue({value: node.value, percentage, formatter}),
  };

  const timeComparisonSegment = {
    extras: {...extras, isTimeComparisonSegment: true},
    color: segmentColor,
    pattern: BarPattern.Striped,
    ...getLabelAndValue({value: node.timeComparisonValue, percentage: timeComparisonPercentage, formatter}),
  };

  const liftComparisonSegment = {
    extras: {
      ...extras,
      isLiftComparisonSegment: true,
      baselineComparisonUnit: node.baselineComparisonUnit,
      baselineComparisonValue: node.baselineComparisonValue,
    },
    color: segmentColor,
    ...getLabelAndValue({
      value: node.liftComparisonValue * 100 ?? 0,
      percentage: node.liftComparisonValue * 100 ?? 0,
      addPercentageSign: true,
    }),
  };

  return {segment, timeComparisonSegment, liftComparisonSegment};
}

export function sumTreeToBarVizSegments({
  formatter,
  groupClauses,
  headers,
  isLiftComparison,
  isStacked,
  isTimeComparison,
  leafSortedKeyMap,
  parentTimeComparisonValue,
  parentValue,
  resultHeaders,
  segmentFilterController,
  showTotal,
  tree,
  valueRepresentationType,
}: {
  formatter: (path: string[], value: number) => string;
  groupClauses: GroupClause[];
  leafSortedKeyMap: {[key: string]: number};
  isStacked: boolean;
  isTimeComparison: boolean;
  isLiftComparison: boolean;
  headers: string[];
  /* parentTimeComparisonValue is used to calculate percentage of total in recursive calls or when multiple events w/ no breakdown */
  parentTimeComparisonValue: number;
  /* parentValue is used to calculate percentage of total in recursive calls or when multiple events w/ no breakdown */
  parentValue: number;
  resultHeaders: string[];
  segmentFilterController?: SegmentFilterController;
  showTotal: boolean;
  tree: OrderedSumTree[];
  valueRepresentationType: ValueRepresentationType;
}): Array<Segment<SegmentExtras>> {
  return tree.map((node) => {
    const segmentProps = {
      segmentName: headers[0],
      label:
        node.label === OTHER_SEGMENT_VALUE
          ? `Other`
          : renamePropertyValue(node.label, resultHeaders[0], groupClauses[0]),
    };

    const relevantNodeValue = (node.unsegmented || node).value;
    const relevantTimeComparisonValue = (node.unsegmented || node).timeComparisonValue;
    const abbreviatedValue = formatter(node.path, relevantNodeValue);

    // for stacked bars, stop recursing at depth - 1 to map segments;
    // also: $overall segments don't have children, so make sure none do
    const isStackedLeaf = isStacked && node.children && !some(node.children, (child) => child.children);

    if (node.children && !isStackedLeaf) {
      return {
        ...segmentProps,
        valueLabel: abbreviatedValue,
        children: sumTreeToBarVizSegments({
          tree: node.children,
          headers: headers.slice(1),
          resultHeaders: resultHeaders.slice(1),
          groupClauses: groupClauses.slice(1),
          leafSortedKeyMap,
          isStacked,
          isTimeComparison,
          isLiftComparison,
          parentValue: relevantNodeValue,
          parentTimeComparisonValue: relevantTimeComparisonValue,
          valueRepresentationType,
          showTotal,
          formatter,
          segmentFilterController,
        }),
      };
    } else if (isStackedLeaf) {
      const mergedSegments = node.children.map((child) =>
        getBarVizBarSegments({
          node: child,
          valueRepresentationType,
          leafSortedKeyMap,
          parentValue: relevantNodeValue,
          parentTimeComparisonValue: relevantTimeComparisonValue,
          formatter,
          segmentFilterController,
        }),
      );
      const segments = [],
        timeComparisonSegments = [],
        liftComparisonSegments = [];

      for (const {segment, timeComparisonSegment, liftComparisonSegment} of mergedSegments) {
        segments.push(segment);
        timeComparisonSegments.push(timeComparisonSegment);
        liftComparisonSegments.push(liftComparisonSegment);
      }

      if (isLiftComparison) {
        const bars: Array<Bar<SegmentExtras>> = [
          {segments: liftComparisonSegments, label: formatMetricNumber(node.value)},
        ];
        return {...segmentProps, bars};
      }

      const bars: Array<Bar<SegmentExtras>> = [{segments, label: abbreviatedValue}];
      if (isTimeComparison) {
        bars.unshift({
          segments: timeComparisonSegments,
          label: formatMetricNumber(node.timeComparisonValue),
        });
      }

      return {...segmentProps, bars};
    } else {
      const {segment, timeComparisonSegment, liftComparisonSegment} = getBarVizBarSegments({
        node,
        valueRepresentationType,
        leafSortedKeyMap,
        parentValue,
        parentTimeComparisonValue,
        formatter,
        segmentFilterController,
      });

      if (isLiftComparison) {
        const bars: Array<Bar<SegmentExtras>> = [{segments: [liftComparisonSegment]}];
        return {
          ...segmentProps,
          bars,
        };
      }

      const bars: Array<Bar<SegmentExtras>> = [{segments: [segment]}];
      if (isTimeComparison) {
        bars.unshift({segments: [timeComparisonSegment]});
      }

      return {
        ...segmentProps,
        bars,
      };
    }
  });
}

export function getMaxLabelCharLengthFromSegments(segments: Array<Segment<SegmentExtras>>): number {
  let maxLabelCharLength = 0;
  const segmentsToTraverse = [...segments];
  while (segmentsToTraverse.length) {
    const segment = segmentsToTraverse.pop();
    if (`bars` in segment) {
      segment.bars.forEach((bar) => {
        bar.segments.forEach((barSegment) => {
          if (barSegment.label.length > maxLabelCharLength) {
            maxLabelCharLength = barSegment.label.length;
          }
        });
      });
    } else if (segment.children) {
      segmentsToTraverse.push(...segment.children);
    }
  }
  return maxLabelCharLength;
}
